import { useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Sun, Moon, LogOut } from 'lucide-react';
import { BrandMark } from '@/components/BrandMark';
import { IconButton, RailLabel, Skeleton, StatusDot } from '@/components/ui';
import { ScopeRail } from '@/components/ScopeRail';
import { useAuth } from '@/auth/auth';
import { useBuckets} from '@/api/hooks';
import { cn } from '@/lib/cn';
import { DEMO } from '@/api/config';
import type { BucketSummary } from '@/api/types';


const stateGroup = (b: BucketSummary): 'review' | 'processing' | 'ready' =>
  b.tone === 'ok' ? 'ready' : b.tone === 'mut' ? 'processing' : 'review';

function BucketRow({ b, active, onClick }: { b: BucketSummary; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'seam flex w-full items-center gap-2 rounded-r px-2 py-1 text-ink-2 transition-colors duration-fast hover:bg-hov hover:text-ink',
        active && 'seam-on text-ink',
      )}
    >
      <StatusDot tone={b.tone} />
      <span className="mono truncate text-caption">{b.name}</span>
    </button>
  );
}

export function Shell() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { data: buckets, isPending } = useBuckets(DEMO);
  const { bucketId } = useParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');

  const groups = useMemo(() => {
    const list = (buckets ?? []).filter((b) => b.name.includes(filter.toLowerCase()));
    return {
      review: list.filter((b) => stateGroup(b) === 'review'),
      processing: list.filter((b) => stateGroup(b) === 'processing'),
      ready: list.filter((b) => stateGroup(b) === 'ready'),
    };
  }, [buckets, filter]);

  // The control drew a sun in both themes, so the one thing it was there to
  // tell you, which theme you are in and which one the press gives you, it did
  // not. Dark is the default, and the state is read from the element the
  // toggle actually writes to.
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    document.documentElement.dataset.theme === 'light' ? 'light' : 'dark',
  );
  const toggleTheme = () => {
    const el = document.documentElement;
    const next = el.dataset.theme === 'light' ? 'dark' : 'light';
    el.dataset.theme = next;
    setTheme(next);
    // index.html hardcodes the dark attribute, so without this a light-theme
    // operator was thrown back to dark by the next reload with no
    // explanation, and the control's own state could only ever read dark on
    // mount. The preference the shell offers is a preference it keeps.
    try {
      localStorage.setItem('poseidon.theme', next);
    } catch {
      // Storage refused; the choice still holds for this page.
    }
  };

  return (
    <div className="grid h-screen grid-cols-[theme(spacing.rail)_1fr]">
      {/* The rail lists every registered pipeline as a focusable control, so
          reaching the work from the keyboard meant traversing the entire
          catalogue. */}
      <a
        href="#main"
        className="sr-only absolute left-2 top-2 z-50 rounded border border-line bg-panel px-3 py-1.5 text-small text-acc-2 focus:not-sr-only"
      >
        Skip to the page
      </a>
      <aside className="flex min-h-0 flex-col border-r border-line bg-panel">
        <NavLink to="/" className="flex items-center gap-2.5 rounded px-3.5 py-3 hover:bg-hov">
          <BrandMark />
          <span className="flex min-w-0 flex-col leading-tight">
            <b className="text-small font-semibold text-ink">Poseidon</b>
            <span className="whitespace-nowrap text-caption text-ink-3">Meridian International</span>
          </span>
        </NavLink>

        {/* One bucket's context, not a directory of every bucket. */}
        {!DEMO && <ScopeRail />}
        {DEMO && (
        <div className="flex min-h-0 flex-1 flex-col border-t border-line px-2.5 pt-2">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find bucket…"
            className="mb-2 w-full rounded border border-line bg-panel-2 px-2.5 py-1.5 text-small text-ink outline-none placeholder:text-ink-4"
          />
          <div className="min-h-0 flex-1 overflow-y-auto pb-4">
            {isPending ? (
              <div className="space-y-1.5 px-2 pt-1">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            ) : (
              (
                [
                  ['Needs review', groups.review],
                  ['Processing', groups.processing],
                  ['Ready', groups.ready],
                ] as const
              ).map(([label, list]) =>
                list.length === 0 ? null : (
                  <div key={label}>
                    <RailLabel count={list.length}>{label}</RailLabel>
                    {list.map((b) => (
                      <BucketRow
                        key={b.id}
                        b={b}
                        active={b.id === bucketId}
                        onClick={() => navigate(`/buckets/${b.id}`)}
                      />
                    ))}
                  </div>
                ),
              )
            )}
          </div>
        </div>
        )}

        <div className="flex items-center gap-2 border-t border-line px-3.5 py-2.5">
          {user?.pictureUrl ? (
            <img src={user.pictureUrl} alt="" className="h-6 w-6 flex-none rounded-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-acc-soft text-caption font-semibold text-acc-2">
              {user?.email[0]?.toUpperCase()}
            </span>
          )}
          <span className="flex min-w-0 flex-col leading-tight">
            {/* The rail is 216px and this sits after an avatar and before two
                icon buttons, so nearly every address renders as an ellipsis.
                In a tool that moves other companies' data, which account you
                are signed in as should be readable. */}
            <b className="truncate text-caption font-medium text-ink" title={user?.email}>
              {user?.email}
            </b>
            <span className="text-caption text-ink-3">{user?.role}</span>
          </span>
          <IconButton
            label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
            className="ml-auto"
            onClick={toggleTheme}
          >
            {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </IconButton>
          <IconButton label="Sign out" onClick={signOut}>
            <LogOut className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </aside>

      <main id="main" tabIndex={-1} className="flex min-h-0 min-w-0 flex-col">
        {/* The API has always said when a session ends; nothing read it, so a
            session simply stopped mid-task. Ten minutes of notice is enough to
            finish the step you are on. */}
        {/* Around the routed page only, so a page that fails to render leaves
            the rail and the sign-out control alive and the operator can walk
            away from it. Keyed on the path: a boundary that latches turns one
            bad page into a dead application. */}
        <ErrorBoundary surface="This page" resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
